import { useRef, useEffect } from "react";

/**
 * Calls `callback` once, the first time the element behind the returned ref
 * intersects the viewport (as configured by `options`), then stops observing.
 *
 * Pass a stable `callback` (useCallback) and a stable `options` object
 * (module-level constant): both are effect dependencies, so a new identity on
 * every render re-creates the observer.
 *
 * Where IntersectionObserver is unavailable (old browsers, jsdom) the callback
 * fires immediately, so gated content still renders instead of never appearing.
 *
 * @param {Function} callback - fired once when the element becomes visible
 * @param {IntersectionObserverInit} [options] - e.g. { rootMargin: "200px" }
 * @returns {React.MutableRefObject} ref to attach to the observed element
 */
const useIntersectionObserver = (callback, options) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      callback();
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        callback();
        observer.disconnect();
      }
    }, options);
    observer.observe(element);

    return () => observer.disconnect();
  }, [callback, options]);

  return elementRef;
};

export default useIntersectionObserver;
